"use client";

import { motion } from "motion/react";
import Link from "next/link";
import type { PublicCatalogProduct } from "@/domain/catalog/catalog-product";
import { homeContent } from "@/data/home-content";
import { ProductImage } from "@/components/catalog/product-image";
import { formatCatalogMoney } from "@/components/home/price";
import { useSubtleInteraction } from "@/components/ui/motion-primitives";

export interface ProductCardProps {
  readonly product: PublicCatalogProduct;
}

const MotionLink = motion.create(Link);

export function ProductCard({ product }: Readonly<ProductCardProps>) {
  const labels = homeContent.productLabels;
  const interaction = useSubtleInteraction();

  return (
    <MotionLink
    className="flex min-h-60 w-40 shrink-0 flex-col rounded-xl border border-outline-variant bg-surface p-2.5 shadow-sm outline-none transition-colors focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 md:w-full md:shrink"
    href={`/productos/${product.slug}`}
    prefetch={false}
      {...interaction}
    >
      <div className="flex h-28 items-center justify-center overflow-hidden rounded-lg bg-white p-[0px]">
        <ProductImage
          alt={product.name}
          className="h-full w-full scale-[1.15] bg-white object-contain"
          imageUrl={product.technicalSheetImageUrl ?? product.primaryImageUrl}
          loading="lazy"
          sizes="160px"
        />
      </div>

      <h3 className="mt-3 line-clamp-2 min-h-10 text-sm font-extrabold leading-5 text-primary-container">
        {product.shortName ?? product.name}
      </h3>

      <div className="mt-auto grid grid-cols-[0.9fr_1.1fr] gap-2 border-t border-surface-container-high pt-2">
        <div className="min-w-0">
          <span className="block text-[10px] font-extrabold uppercase text-muted">
            {labels.unit}
          </span>
          <span className="text-[15px] font-extrabold text-primary-container">
            {formatCatalogMoney(product.salePrice, product.currencyCode)}
          </span>
        </div>
        <div className="min-w-0 border-l border-surface-container-high pl-2">
          <span className="block text-[10px] font-extrabold uppercase text-muted">
            {labels.wholesale}
          </span>
          {product.wholesalePrice ? (
            <>
              <span className="block text-lg font-extrabold leading-5 text-secondary">
                {formatCatalogMoney(product.wholesalePrice, product.currencyCode)}
              </span>
              {product.wholesaleMinQuantity ? (
                <span className="block text-[10px] leading-3 text-muted">
                  {labels.wholesaleFrom} {product.wholesaleMinQuantity}{" "}
                  {labels.units}
                </span>
              ) : null}
            </>
          ) : (
            <span className="block text-sm font-bold text-muted">-</span>
          )}
        </div>
      </div>
    </MotionLink>
  );
}
